import {useEffect, useMemo, useState, useCallback, useRef, useContext} from "react"
import { ItemWarehouse } from './api/itemwarehouse'
import { useParams, useNavigate } from "react-router-dom";
import {PASDetailedBody} from "./PASDetailedBody"
import "./PASDetailed.scss"
import {FullScreenButton} from "./FullScreenButton"
import {useDivision} from "./shared"
import {AllDataContext} from "./LoggedinMainPage"
import SearchableSelect from "./inputs/SearchableSelect";

function parseInt10WithDefault(s: string | undefined, defaultValue: number): number {
  if (s !== undefined && /^-?[0-9]+$/.test(s)) {
    return parseInt(s, 10)
  }
  return defaultValue
}

function isPositiveInt(s: string): boolean {
  return /^[1-9][0-9]*$/.test(s)
}

export default function PASDetailed(){
  const allData = useContext(AllDataContext)
  const warehouses = allData.warehouses
  const division = useDivision()

  const [warningLevel, setWarningLevel] = useState("")
  const [infoLevel, setInfoLevel] = useState("")

  const warehouseCode = useParams().warehouseCode ?? "-"
  const itemCode = useParams().itemCode ?? "-"
  const warningLevelParam = (
    useParams().warningLevel ??
      "0" + ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL.toString()).slice(1)
  const infoLevelParam = (
    useParams().infoLevel ??
      "0" + ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL.toString()).slice(1)
  const navigate = useNavigate()

  useEffect(() => {
    setWarningLevel(warningLevelParam);
  }, [warningLevelParam])

  useEffect(() => {
    setInfoLevel(infoLevelParam);
  }, [infoLevelParam])

  const itemWarehouses = allData.itemWarehouses.filter(
    iw => iw.warehouseCode === warehouseCode)

  const doNavigate = useCallback(
    ({newWarehouseCode, newItemCode, newWarningLevel, newInfoLevel, replace}:
      {newWarehouseCode?: string, newItemCode?: string
       newWarningLevel?: string, newInfoLevel?: string, replace?: boolean}) => {
      if (newWarehouseCode !== undefined && newItemCode === undefined) {
        newItemCode = "-"
      }
      if (newItemCode !== undefined && newWarningLevel === undefined) {
        newWarningLevel = ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL.toString()
      }
      if (newItemCode !== undefined && newInfoLevel === undefined) {
        newInfoLevel = ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL.toString()
      }
      navigate([
        "",
        "division",
        division,
        "PASDetailed",
        newWarehouseCode ?? warehouseCode,
        newItemCode ?? itemCode,
        // stupid system doesn't know how to work with empty path segments
        "0" + (newWarningLevel ?? warningLevel),
        "0" + (newInfoLevel ?? infoLevel),
      ].map(encodeURIComponent).join("/"), {replace: replace ?? false})
    }, [navigate, division, warehouseCode, itemCode, warningLevel, infoLevel])

  useEffect(function setToMainWarehouseIfNoValidWarehouseSelected() {
    if (warehouses.find(w => w.code === warehouseCode)) {
      return
    }
    const mainWarehouse = warehouses.find(w => w.main === 1) ?? warehouses[0]
    if (mainWarehouse) {
      doNavigate({newWarehouseCode: mainWarehouse.code, replace: true})
    }
  }, [doNavigate, division, warehouseCode, warehouses])

  useEffect(function resetItemCodeIfItemCodeNotFoundInCurrentWarehouse() {
    if (!warehouses.find(w => w.code === warehouseCode)) {
      return
    }
    if (itemCode === "-") {
      return
    }
    if (!itemWarehouses.find(w => w.itemCode === itemCode)) {
      doNavigate({newItemCode: "-", replace: true})
    }
  }, [doNavigate, division, itemCode, itemWarehouses,
    warehouses, warehouseCode])

  const optionsKeyAndValue = useMemo(() => allData.items.map(item => [
      item.code, item.code + " \u2014 " + item.description] as [string, string])
  , [allData.items])

  const selectedItemWarehouse = itemWarehouses.find(iw => iw.itemCode === itemCode)
  const thisCard = useRef<HTMLDivElement>(null)

  return <div className="PASDetailed">
    <div className="warehouseSelector">
        <span>Select a warehouse: </span>
          <select data-cy="select-warehouse" value={warehouseCode} onChange={
            e => doNavigate({newWarehouseCode: e.target.value})}>
            {[...warehouses].sort((a, b) => a.code.localeCompare(b.code)).map(
              warehouse => <option key={warehouse.code} value={warehouse.code}>
                {warehouse.code} - {warehouse.description}
                </option>
            )}
          </select>
    </div>
    <div className="itemSelector">
      <span>Select an item: </span>
      <SearchableSelect
        optionsKeyAndValue={[["-", "Select an item"], ...optionsKeyAndValue]}
        value={itemCode}
        data-cy="select-item"
        onChange={
          newValue => doNavigate({
            newItemCode: newValue,
            newWarningLevel:
            ItemWarehouse.DEFAULT_WARNING_STOCK_LEVEL.toString(),
            newInfoLevel: ItemWarehouse.DEFAULT_INFO_STOCK_LEVEL.toString(),
        })} />
    </div>
    <table>
      <thead>
        <tr>
          <th>Warning Level</th>
          <th>Info Level</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <input data-cy="warninglevel" type="text" value={warningLevel}
              className={isPositiveInt(warningLevel) ? "" : "error"}
              onChange={e => setWarningLevel(e.target.value)}
              onBlur={e => doNavigate({newWarningLevel: e.target.value})}
            />
          </td>
          <td>
            <input data-cy="infolevel" type="text" value={infoLevel.toString()}
              className={isPositiveInt(infoLevel) ? "" : "error"}
              onChange={e => setInfoLevel(e.target.value)}
              onBlur={e => doNavigate({newInfoLevel: e.target.value})}
            />
          </td>
        </tr>
      </tbody>
    </table>
    {selectedItemWarehouse &&
    <>
      <hr />
      <div ref={thisCard}>
        <FullScreenButton elementToFullScreen={thisCard} />
        <PASDetailedBody
          itemWarehouse={selectedItemWarehouse}
          // use 1 as default, since most likely empty string
          warningLevel={parseInt10WithDefault(warningLevel, 1)}
          infoLevel={parseInt10WithDefault(infoLevel, 1)} />
      </div>
    </>
    }
  </div>
}
